import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router';
import {
	Box,
	Typography,
	Switch,
	IconButton,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Tooltip,
	Button,
	useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import { authedApiFetch } from '../../../../utils/Api';
import PipelineSettingsDrawer from './PipelineSettingsDrawer';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { StyledDataGridContainer } from '../common/StyledDataGridContainer';

const PipelineSettings = ({ showNotification }) => {
	const { tenantId } = useParams();
	const theme = useTheme();
	const [isLoading, setIsLoading] = useState(false);
	const [stages, setStages] = useState([]);
	const [configListId, setConfigListId] = useState(null);
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedRowForMenu, setSelectedRowForMenu] = useState(null);

	// Confirmation dialog state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [stageToDelete, setStageToDelete] = useState(null);

	// Stage drawer state (for both add and edit)
	const [addStageDrawerOpen, setAddStageDrawerOpen] = useState(false);
	const [editStageDrawerOpen, setEditStageDrawerOpen] = useState(false);
	const [editStageData, setEditStageData] = useState({
		displayName: '',
		description: '',
		sortOrder: 0,
		active: true,
	});

	const [newStageData, setNewStageData] = useState({
		displayName: '',
		description: '',
		sortOrder: 0,
		active: true,
	});

	const valueFromDisplayName = (displayName) =>
		displayName.toLowerCase().replace(/\s+/g, '_');

	const fetchPipelineStages = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists?list_type=deal_stages`,
				method: 'GET',
			});

			if (response?.[0]?.config_list_items) {
				setConfigListId(response[0].id);

				const sortedItems = response[0].config_list_items.sort(
					(a, b) => a.sort_order - b.sort_order
				);
				const gridData = sortedItems.map((item) => ({
					id: item.id,
					displayName: item.display_name || '',
					value: item.value || '',
					description: item.description || '',
					sortOrder: item.sort_order || 0,
					active: item.active,
					_original: item,
				}));
				setStages(gridData);
			} else {
				showNotification('Failed to load pipeline stages', 'error', 6000);
			}
		} catch (error) {
			showNotification('Error loading pipeline stages', 'error', 6000);
		} finally {
			setIsLoading(false);
		}
	}, [tenantId]);

	// Menu handlers
	const handleOpenMenu = (event, row) => {
		setAnchorEl(event.currentTarget);
		setSelectedRowForMenu(row);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedRowForMenu(null);
	};

	// Handle menu item click
	const handleMenuItemClick = (action) => {
		setAnchorEl(null);
		if (action === 'delete') {
			setStageToDelete(selectedRowForMenu);
			setDeleteDialogOpen(true);
		} else if (action === 'edit') {
			// Initialize edit form data with the selected stage's values
			setEditStageData({
				displayName: selectedRowForMenu.displayName || '',
				description: selectedRowForMenu.description || '',
				sortOrder: selectedRowForMenu.sortOrder || 0,
				active:
					selectedRowForMenu.active !== undefined
						? selectedRowForMenu.active
						: true,
				id: selectedRowForMenu.id, // config_list_item ID needed for api call
				value: selectedRowForMenu.value || '',
			});
			setEditStageDrawerOpen(true);
		}
	};

	// Delete stage
	const deleteStage = async (stage) => {
		const config_list_id = configListId;
		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists/${config_list_id}/config_list_items/${stage.id}`,
				method: 'DELETE',
			});
			await fetchPipelineStages();
			return true;
		} catch (error) {
			console.error('Error deleting stage:', error);
			return false;
		}
	};

	const handleDeleteConfirm = async () => {
		if (!stageToDelete || !configListId) return;

		try {
			const success = await deleteStage(stageToDelete);
			if (success) {
				showNotification('Pipeline stage deleted successfully', 'success');
			} else {
				showNotification('Error deleting pipeline stage', 'error', 6000);
			}
		} catch (error) {
			console.error('Error deleting stage:', error);
			showNotification('Error deleting pipeline stage', 'error', 6000);
		} finally {
			setDeleteDialogOpen(false);
			setStageToDelete(null);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteDialogOpen(false);
		setStageToDelete(null);
	};

	// Add Stage handlers
	const handleAddStageClick = () => {
		setNewStageData({
			displayName: '',
			description: '',
			sortOrder: stages.length + 1,
			active: true,
		});
		setAddStageDrawerOpen(true);
	};

	const handleAddStageClose = () => {
		setAddStageDrawerOpen(false);
	};

	const handleNewStageChange = (e) => {
		const { name, value } = e.target;
		setNewStageData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	// Edit Stage handlers
	const handleEditStageClose = () => {
		setEditStageDrawerOpen(false);
	};

	const handleEditStageChange = (e) => {
		const { name, value } = e.target;
		setEditStageData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleEditStageSubmit = async () => {
		if (!editStageData.displayName) return;

		try {
			const updatedStage = {
				displayName: editStageData.displayName,
				description: editStageData.description,
				sortOrder: editStageData.sortOrder,
				active: editStageData.active,
				value: valueFromDisplayName(editStageData.displayName),
				id: editStageData.id, // config_list_item ID needed for api call
			};

			await updateStage(updatedStage);
			await fetchPipelineStages();
			showNotification('Pipeline stage updated successfully', 'success');
			handleEditStageClose();
		} catch (error) {
			console.error('Error updating stage:', error);
			showNotification('Error updating pipeline stage', 'error', 6000);
		}
	};

	const handleAddStageSubmit = async () => {
		if (!configListId) return;

		try {
			// Prepare payload
			const payload = {
				config_list_item: {
					display_name: newStageData.displayName,
					value: newStageData.displayName.toLowerCase().replace(/\s+/g, '_'),
					description: newStageData.description,
					sort_order: newStageData.sortOrder,
					active: newStageData.active,
				},
			};

			// API call to create new stage
			await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists/${configListId}/config_list_items`,
				method: 'POST',
				payload,
			});

			// Refetch stages to update the list
			await fetchPipelineStages();
			showNotification('Pipeline stage created successfully', 'success');
			setAddStageDrawerOpen(false);
		} catch (error) {
			console.error('Error creating stage:', error);
			showNotification('Error creating pipeline stage', 'error', 6000);
		}
	};

	const updateStage = async (stage) => {
		if (!configListId) return;

		try {
			// If this is a default stage, the backend will handle cloning for customization
			const endpoint = `/tenants/${tenantId}/config_lists/${configListId}/config_list_items/${stage.id}`;

			await authedApiFetch({
				endpoint,
				method: 'PUT',
				payload: {
					config_list_item: {
						display_name: stage.displayName,
						sort_order: stage.sortOrder,
						value: stage.value || valueFromDisplayName(stage.displayName),
						active: stage.active,
						description: stage.description,
					},
				},
			});

			// Get the updated list with proper sort order
			await fetchPipelineStages();

			showNotification('Changes saved', 'success');
		} catch (error) {
			console.error('Error saving stage:', error);
			showNotification('Error saving stage', 'error', 6000);
		}
	};

	const handleActiveToggle = async (event, row) => {
		const updatedRow = {
			...row,
			active: event.target.checked,
		};

		setStages((prevStages) =>
			prevStages.map((stage) =>
				stage.id === updatedRow.id ? updatedRow : stage
			)
		);

		await updateStage(updatedRow);
	};

	const handleRowUpdate = async (newRow, oldRow) => {
		if (
			newRow.displayName === oldRow.displayName &&
			newRow.description === oldRow.description &&
			newRow.sortOrder === oldRow.sortOrder &&
			newRow.active === oldRow.active
		) {
			return oldRow;
		}

		setStages((prevStages) =>
			prevStages.map((stage) => (stage.id === newRow.id ? newRow : stage))
		);

		await updateStage(newRow);

		return newRow;
	};

	const columns = [
		{
			field: 'actions',
			headerName: '',
			width: 70,
			sortable: false,
			renderCell: (params) => (
				<Box>
					<Tooltip title='Actions'>
						<IconButton
							onClick={(event) => handleOpenMenu(event, params.row)}
							size='small'
							aria-label='stage actions'
						>
							<MoreVertIcon />
						</IconButton>
					</Tooltip>

					<Menu
						anchorEl={anchorEl}
						open={Boolean(anchorEl) && selectedRowForMenu?.id === params.row.id}
						onClose={handleCloseMenu}
						PaperProps={{
							elevation: 1,
							sx: { minWidth: 180 },
						}}
					>
						<MenuItem onClick={() => handleMenuItemClick('edit')}>
							<ListItemIcon>
								<EditIcon fontSize='small' />
							</ListItemIcon>
							<ListItemText>Edit Stage</ListItemText>
						</MenuItem>
						<MenuItem onClick={() => handleMenuItemClick('delete')}>
							<ListItemIcon>
								<DeleteIcon fontSize='small' color='error' />
							</ListItemIcon>
							<ListItemText>Delete Stage</ListItemText>
						</MenuItem>
					</Menu>
				</Box>
			),
		},
		{ field: 'displayName', headerName: 'Name', flex: 1, editable: true },
		{
			field: 'description',
			headerName: 'Description',
			flex: 1.5,
			editable: true,
		},
		{
			field: 'active',
			headerName: 'Active',
			width: 100,
			editable: true,
			renderCell: (params) => (
				<Switch
					checked={params.row.active}
					onChange={(e) => handleActiveToggle(e, params.row)}
					color='primary'
				/>
			),
		},
		{
			field: 'sortOrder',
			headerName: 'Sort Order',
			type: 'number',
			width: 120,
			editable: true,
		},
	];

	useEffect(() => {
		fetchPipelineStages();
	}, [fetchPipelineStages]);

	return (
		<Box sx={{ p: 3 }}>
			<Typography variant='h6'>Pipeline Stages</Typography>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 2,
				}}
			>
				<Typography variant='body2' color='text.secondary'>
					Define the stages used in your investment process.
				</Typography>
				<Button
					variant='contained'
					color='primary'
					size='small'
					onClick={handleAddStageClick}
				>
					Add Stage
				</Button>
			</Box>

			<StyledDataGridContainer>
				<DataGrid
					rows={stages}
					columns={columns}
					loading={isLoading}
					pageSize={5}
					rowsPerPageOptions={[5, 10, 25]}
					processRowUpdate={handleRowUpdate}
					experimentalFeatures={{ newEditingApi: true }}
					disableSelectionOnClick
					hideFooter
					initialState={{
						sorting: {
							sortModel: [{ field: 'sort_order', sort: 'asc' }],
						},
					}}
				/>
			</StyledDataGridContainer>

			{/* Delete Confirmation Dialog */}
			<ConfirmationDialog
				open={deleteDialogOpen}
				handleConfirm={handleDeleteConfirm}
				handleCancel={handleDeleteCancel}
				title='Delete Pipeline Stage'
				message='Are you sure you want to delete this pipeline stage? This action cannot be undone.'
				confirmActionButtonLabel='Delete'
				confirmActionButtonColor='error'
				cancelActionButtonColor='primary'
			/>

			{/* Add Stage Drawer */}
			<PipelineSettingsDrawer
				open={addStageDrawerOpen}
				onClose={handleAddStageClose}
				onSubmit={handleAddStageSubmit}
				formData={newStageData}
				onChange={handleNewStageChange}
				isMobile={useMediaQuery(theme.breakpoints.down('sm'))}
			/>
			{/* Edit Stage Drawer */}
			<PipelineSettingsDrawer
				open={editStageDrawerOpen}
				onClose={handleEditStageClose}
				onSubmit={handleEditStageSubmit}
				formData={editStageData}
				onChange={handleEditStageChange}
				isMobile={useMediaQuery(theme.breakpoints.down('sm'))}
				editMode={true}
			/>
		</Box>
	);
};

export default PipelineSettings;
